import React from "react";
import { useState } from "react";
import { Box, Card, Grid, Stack, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import RiskListPopUp from "./RiskListPopUp";
import RiskPanelHeader from "./RiskPanelHeader";
import { overviewChartPalette } from "styles/theme";

const usedColors = new Set();

const generateFallbackColor = (text) => {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
        hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }

    const goldenAngle = 137.508;

    // Allowed hue ranges (blue → purple only)
    const ranges = [
        [180, 240], // cyan-blue
        [260, 320], // purple-magenta
    ];

    let attempt = 0;
    let hex;

    do {
        const range = ranges[Math.abs(hash + attempt) % ranges.length];
        const hue =
            range[0] +
            ((Math.abs(hash) + attempt * goldenAngle) %
                (range[1] - range[0]));

        const saturation = 65;
        const lightness = 50;

        hex = hslToHex(hue, saturation, lightness);
        attempt++;
    } while (usedColors.has(hex) && attempt < 10);

    usedColors.add(hex);
    return hex;
};


const hslToHex = (h, s, l) => {
    s /= 100;
    l /= 100;

    const k = (n) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n) =>
        l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

    return (
        "#" +
        [f(0), f(8), f(4)]
            .map((x) =>
                Math.round(255 * x)
                    .toString(16)
                    .padStart(2, "0")
            )
            .join("")
    );
};


const RiskAgeingAnalysis = ({ data }) => {

    const [isView, setIsView] = useState(false);
    const [rowData, setRowData] = useState(null);

    const percentage = data?.data?.totalActiveRisks ?? 0; // dynamic value

    const palette = [overviewChartPalette.teal, overviewChartPalette.cyan, overviewChartPalette.amber, overviewChartPalette.coral];
    const chartData = data?.data?.buckets?.map((item, index) => ({
        y: item.count,              // or item.percentage if required
        name: item.label,
        color: palette[index % palette.length],
    }));

    const handleGetRisk = (point) => {
        setIsView((prev) => {
            const newState = !prev;
            setRowData(newState ? { Ageing: point } : null);
            return newState;
        });
    }

    const handleClose = () => {
        setIsView(false)
        setRowData(null)
    }

    const options = {
        chart: {
            type: "pie",
            height: 300,
            backgroundColor: "transparent",
            margin: 0
        },

        title: {
            // text: `<div style="text-align:center">
            //         <span style="font-size:22px;font-weight:400;font-family:Outfit; color:#000000">${percentage}</span><br/>
            //         <span style="font-size:14px;color:#00000099";font-family:Outfit;>Active Risk</span>
            //       </div>`,
            text: '',
            align: "center",
            verticalAlign: "middle",
            useHTML: true,
            y: 10
        },

        tooltip: {
            enabled: true
        },

        plotOptions: {
            pie: {
                innerSize: "75%",
                size: '100%',
                // minSize:60,
                borderWidth: 0,
                borderRadius: 0,
                borderColor: "#fff",
                dataLabels: {
                    enabled: false
                },
                cursor: "pointer",
                point: {
                    events: {
                        click: function () {
                            handleGetRisk(this.name);
                        }
                    }
                }
            }
        },

        series: [
            {
                name: "Risk Ageing Analysis",
                data: chartData
            },
        ],

        credits: {
            enabled: false
        },

        legend: {
            enabled: false
        }
    };

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 3,
                border: "1px solid #E4E4E4",
                width: '100%',
                height: { xs: "auto", md: 450 },
                boxSizing: "border-box",
            }}
        >
            <RiskPanelHeader eyebrow="Time exposure" title="Risk Ageing" subtitle="Identify unresolved risks accumulating beyond expected closure periods." />
            <Box sx={{
                width: '100%',
                borderBottom: "1px solid #E4E4E4",
                p: 1.5,
                display: 'none',
                justifyContent: 'space-between'
            }}>
                <Box>
                    <Typography variant="subtitle1" sx={{
                        fontFamily: "Outfit, sans-serif",
                        fontWeight: 600,
                        fontSize: '20px',
                        letterSpacing: "0px",
                        mb: 0
                    }}>
                        Risk Ageing Analysis
                    </Typography>
                    <Typography variant="body2" sx={{
                        fontFamily: "Outfit, sans-serif",
                        fontWeight: 300,
                        fontSize: '16px',
                        letterSpacing: "0px",
                        color: '#7D7F81'
                    }}>
                        Groups risks by type to show which areas of the project are most exposed.
                    </Typography>
                </Box>
            </Box>
            <Grid container sx={{ alignItems: "center", justifyContent: "space-between", px: 2 }}>
                <Grid size={{ md: 6 }}>
                    <Stack spacing={4} sx={{ pt: 1.5, pl: 1.5, pb: 1.5, maxHeight: "340px", minHeight: '340px', overflowY: "auto", justifyContent: 'center' }} >
                        {chartData?.map((item, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    border: "1px solid #EAEAEA",
                                    borderRadius: "4px",
                                    padding: "8px 10px",
                                    backgroundColor: "#fff",
                                }}
                            >
                                {/* Left Section */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: "50%",
                                            backgroundColor: item?.color,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: "16px",
                                            fontWeight: 400,
                                            color: "#010814",
                                            fontFamily: "Outfit, sans-serif",
                                            lineHeight: '18px',
                                            verticalAlign: 'middle'
                                        }}
                                    >
                                        {item?.name}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        minWidth: 40,
                                        height: 18,
                                        borderRadius: "5px",
                                        backgroundColor: "#E7E7EB",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        color: "#433F5E",
                                        fontFamily: "Outfit, sans-serif",
                                    }}
                                >
                                    {String(item?.y).padStart(2, "0")}
                                </Box>
                            </Box>
                        ))}
                    </Stack>

                </Grid>
                <Grid size={{ md: 6 }}>
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "flex-end",
                        }}
                    >
                        <HighchartsReact highcharts={Highcharts} options={options} />
                    </Box>
                </Grid>
            </Grid>
            {isView &&
                <RiskListPopUp header = "Risk Ageing Analysis"  open={isView} data={rowData} handleClose={handleClose} />
            }
        </Card>
    );
};

export default RiskAgeingAnalysis;
